import { createContext, useMemo, useEffect, useState, useCallback } from "react";

import { useDisclosure } from "@chakra-ui/react";
import type { StickyBottomBarContextProps, StickyBottomBarContextProviderProps } from "./StickyBottomBarContext.d";
import { isBottomBarClosed, setBottomBarSessionState } from "./utils";

export const StickyBottomBarContext = createContext<StickyBottomBarContextProps | undefined>(undefined);

export const StickyBottomBarContextProvider = ({ threshold = 0, children }: StickyBottomBarContextProviderProps) => {
  const { isOpen, onOpen: showBottomBar, onClose: hideBottomBar } = useDisclosure();
  const [firstTriggerElement, setFirstTriggerElement] = useState<Element>(null);
  const [secondTriggerElement, setSecondTriggerElement] = useState<Element>(null);

  useEffect(() => {
    const callBack = (entries: IntersectionObserverEntry[]) =>
      entries.every(({ target, isIntersecting }) =>
        isBottomBarClosed() || (target && isIntersecting) ? hideBottomBar() : showBottomBar()
      );

    const observer = new IntersectionObserver(callBack, { threshold });

    if (firstTriggerElement) {
      observer.observe(firstTriggerElement);
    }

    if (secondTriggerElement) {
      observer.observe(secondTriggerElement);
    }

    return () => {
      observer.disconnect();
    };
  }, [firstTriggerElement, hideBottomBar, secondTriggerElement, showBottomBar, threshold]);

  const closeBottomBar = useCallback(() => {
    setBottomBarSessionState();
    hideBottomBar();
  }, [hideBottomBar]);

  const value = useMemo(
    () => ({
      isOpen,
      closeBottomBar,
      setFirstTriggerElement,
      setSecondTriggerElement,
    }),
    [isOpen, closeBottomBar, setFirstTriggerElement, setSecondTriggerElement]
  );

  return <StickyBottomBarContext.Provider value={value}>{children}</StickyBottomBarContext.Provider>;
};
